import { useEffect, useState } from "react";
import { Download, X, Share2, Plus, Shield } from "lucide-react";

/**
 * Admin-only PWA install prompt.
 *
 * Only renders when:
 *  • an admin JWT is present in localStorage AND
 *  • the user is currently on an /admin route AND
 *  • the app is not already installed (display-mode: standalone)
 *
 * On mount, swaps <link rel="manifest"> to point at /manifest-admin.json
 * so the platform install dialog announces "Install ScottsClub Admin" with
 * the admin icon, and the installed shortcut launches directly at /admin.
 *
 * iOS Safari has no beforeinstallprompt event, so we surface an instructional
 * modal with the Share-icon → Add to Home Screen flow.
 */
const MANIFEST_PUBLIC = "/manifest.json";
const MANIFEST_ADMIN = "/manifest-admin.json";
const SESSION_DISMISS_KEY = "sc_admin_pwa_dismissed_session";

function setManifest(href) {
    let el = document.querySelector('link[rel="manifest"]');
    if (!el) {
        el = document.createElement("link");
        el.rel = "manifest";
        document.head.appendChild(el);
    }
    if (el.getAttribute("href") !== href) el.setAttribute("href", href);
}

function keepAdminManifest() {
    setManifest(MANIFEST_ADMIN);
}

function isIosSafari() {
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    return isIos && isSafari;
}

export default function AdminInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [iosModal, setIosModal] = useState(false);
    const [dismissed, setDismissed] = useState(
        sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"
    );

    useEffect(() => {
        // Keep the admin manifest active while on admin routes. The inline script
        // in index.html already set it before React mounted; we re-assert here in
        // case someone navigated in via client-side routing. NO cleanup — reverting
        // to the public manifest mid-session (e.g. during /admin -> /admin/login
        // redirect) would break the install target.
        setManifest(MANIFEST_ADMIN);
    }, []);

    useEffect(() => {
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone;
        if (standalone) setInstalled(true);

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        const onInstalled = () => setInstalled(true);
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = () => {
        sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
        setDismissed(true);
    };

    const install = async () => {
        if (deferred) {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice?.outcome === "accepted") setInstalled(true);
            setDeferred(null);
            return;
        }
        if (isIosSafari()) setIosModal(true);
    };

    if (installed || dismissed) return null;

    const ios = isIosSafari();
    const canInstall = !!deferred || ios;
    if (!canInstall) return null;

    return (
        <>
            <div
                className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,460px)]"
                data-testid="admin-pwa-install"
            >
                <div className="glass-card rounded-sm p-4 flex items-center gap-3 border-red-700/40">
                    <div className="w-10 h-10 rounded-full border border-red-500/40 bg-red-900/20 flex items-center justify-center text-red-300 flex-shrink-0">
                        <Shield size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-base text-ink-primary leading-tight">
                            Install Admin App
                        </div>
                        <div className="text-[11px] text-ink-secondary mt-0.5 leading-tight">
                            One-tap door & queue control on your home screen.
                        </div>
                    </div>
                    <button
                        onClick={install}
                        data-testid="admin-pwa-install-btn"
                        className="btn-gold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] tracking-[0.2em] uppercase font-medium"
                    >
                        <Download size={12} /> Install
                    </button>
                    <button
                        onClick={dismiss}
                        data-testid="admin-pwa-dismiss"
                        aria-label="Dismiss"
                        className="text-ink-secondary hover:text-gold"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {iosModal && (
                <div
                    className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 pb-4"
                    onClick={() => setIosModal(false)}
                    data-testid="ios-install-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md p-6 rounded-sm glass-strong gold-border-anim"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-serif text-xl inline-flex items-center gap-2">
                                <Shield size={16} className="text-gold" /> Install Admin App
                            </h3>
                            <button onClick={() => setIosModal(false)} className="text-ink-secondary hover:text-gold">
                                <X size={18} />
                            </button>
                        </div>
                        <ol className="space-y-3 text-ink-secondary text-sm">
                            <li className="flex items-start gap-3">
                                <span className="font-serif text-gold text-lg leading-none">1</span>
                                <span className="flex items-center gap-2">
                                    Tap the Share button <Share2 size={14} className="inline text-gold" /> at the bottom of Safari.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="font-serif text-gold text-lg leading-none">2</span>
                                <span className="flex items-center gap-2">
                                    Choose <span className="text-ink-primary font-medium">Add to Home Screen</span>
                                    <Plus size={14} className="inline text-gold" />.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="font-serif text-gold text-lg leading-none">3</span>
                                <span>
                                    Tap <span className="text-ink-primary font-medium">Add</span>. The Admin app icon will appear on your home screen.
                                </span>
                            </li>
                        </ol>
                        <button
                            onClick={() => setIosModal(false)}
                            data-testid="ios-modal-close"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-xs tracking-[0.22em] uppercase"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
